/**
 * Function to preprocess numbers in an array of row objects for visualization.
 *
 * @param {Array<Object>} rows - Array of row objects (e.g., {id: 1, col1: 'Hello', col2: 123.456}).
 * @param {Object} options - Options for formatting the numbers.
 * @param {number} options.decimalPlaces - Number of decimal places to show (default: 2).
 * @param {string} options.scientific - "auto", "always", or "never" to control scientific notation (default: "auto").
 * @param {number} options.scientificThreshold - Threshold for switching to scientific notation in "auto" mode (default: 1e6).
 * @param {number} options.smallNumberThreshold - Threshold for switching to scientific notation for small numbers in "auto" mode (default: 1e-4).
 * @returns {Array<Object>} - Array of row objects with formatted numbers.
 */

const DEBUG = false;

function preprocessRows(rows, options = {}) {
	const {
		decimalPlaces = 2, // Default to 2 decimal places
		scientific = 'auto', // Default to auto scientific notation
		scientificThreshold = 1e6, // Default threshold for large numbers in "auto"
		smallNumberThreshold = 1e-4, // Default threshold for small numbers in "auto"
		thousandSeparator = ' ', // Default to space separator
		decimalSeparator = '.', // Default to dot separator
	} = options;

	// Helper function to format numbers with custom separators
	const applyNumberFormat = (numStr) => {
		const parts = numStr.split('.');
		// Add thousand separators
		parts[0] = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, thousandSeparator);
		// Replace decimal separator if needed
		return parts.join(decimalSeparator);
	};

	// Helper function to process individual numbers
	const formatNumber = (num) => {
		if (typeof num !== 'number') return num; // Return if not a number

		// If it's an integer, format with thousand separators only
		if (Number.isInteger(num)) {
			return applyNumberFormat(num.toString());
		}

		// Determine if scientific notation should be applied
		let useScientific = false;

		if (scientific === 'always') {
			useScientific = true;
		} else if (scientific === 'auto') {
			// Auto mode, apply scientific notation if the number is too large or small
			useScientific =
				Math.abs(num) >= scientificThreshold || (num !== 0 && Math.abs(num) < smallNumberThreshold);
		}

		// Format the number based on the condition
		if (useScientific) {
			return applyNumberFormat(num.toExponential(decimalPlaces));
		} else {
			return applyNumberFormat(num.toFixed(decimalPlaces));
		}
	};

	// Loop over each row and each key in the row
	return rows.map((row) => {
		const newRow = { ...row }; // Create a shallow copy of the row
		Object.keys(newRow).forEach((key) => {
			// Check if the value is a number and process it if it is
			const value = newRow[key];
			if (typeof value === 'number') {
				newRow[key] = formatNumber(value);
			}
		});
		return newRow;
	});
}

export default function datagridConverter(data, options) {
	const newData = { ...data };

	newData.columns.forEach((e) => {
		e.headerName = e.title;
		e.flex = 1;
	});

	newData.data.forEach((e, i) => {
		e.id = i;
	});

	if (DEBUG) console.log(newData);

	// Add a 'type' property to each column in newData.columns if all row values are numeric
	newData.columns.forEach((column) => {
		const columnKey = column.field; // Assume `field` represents the key of the data
		let allNumeric = true;

		for (let i = 0; i < newData.data.length; i++) {
			const value = newData.data[i][columnKey];
			if (value === null || value === undefined) continue; // ignore null/undefined
			if (!(typeof value === 'number' || (!isNaN(parseFloat(value)) && isFinite(value)))) {
				allNumeric = false;
				break; // Exit loop early if any non-numeric is found
			}
		}

		if (allNumeric) {
			column.type = 'number';

			// Add custom valueFormatter with configurable separators
			const { thousandSeparator = ' ', decimalSeparator = '.' } = options;
			column.valueFormatter = (params) => {
				if (params.value == null || params.value === '') return '';
				const parts = params.value.toString().split('.');
				parts[0] = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, thousandSeparator);
				return parts.join(decimalSeparator);
			};

			// Add custom sortComparator to handle numeric sorting of formatted strings
			column.sortComparator = (v1, v2) => {
				// Remove thousand separators and parse as numbers for sorting
				const num1 = typeof v1 === 'string' ? parseFloat(v1.replace(/\s/g, '')) : parseFloat(v1);
				const num2 = typeof v2 === 'string' ? parseFloat(v2.replace(/\s/g, '')) : parseFloat(v2);
				return num1 - num2;
			};

			// Replace null/undefined with 0 in numeric columns
			for (let i = 0; i < newData.data.length; i++) {
				if (newData.data[i][columnKey] == null) {
					newData.data[i][columnKey] = 0;
				}
			}
		}
	});

	newData.data = preprocessRows(newData.data, options);

	if (DEBUG) console.log(newData);

	return newData;
}
